import { useEffect, useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { shaktiApi, ApiError } from '../api/client';
import { useAuthStore } from '../stores/auth';
import { useProjectStore } from '../stores/project';

/**
 * K1.1 (round-5) — first-run onboarding wizard.
 *
 * Round-4 shipped /login + /dashboard/settings + NewProjectModal +
 * StartBuildingPanel. A brand-new signed-in user could still get lost
 * because nothing orchestrated those three steps. This wizard ties
 * them together:
 *
 *   1. Welcome — "Let's get you shipping in 4 clicks."
 *   2. Connect an LLM provider (Anthropic / OpenAI / Google / etc.).
 *   3. Create your first project.
 *
 * After step 3 completes, navigate to `/dashboard` where the
 * StartBuildingPanel is primed for intent capture.
 *
 * Redirect logic lives in AuthBoundary: when signed in + zero projects
 * + zero providers, push to `/onboarding`. Once either condition flips
 * (first project OR first provider), the redirect no longer fires and
 * the user reaches `/dashboard` directly.
 */
type Step = 'welcome' | 'connect_llm' | 'create_project' | 'first_task' | 'done';

const PROVIDER_CHOICES: ReadonlyArray<{
  kind: string;
  label: string;
  placeholder: string;
  hint: string;
}> = [
  {
    kind: 'anthropic',
    label: 'Anthropic (Claude)',
    placeholder: 'sk-ant-…',
    hint: 'Best for long-context reasoning. Get a key at console.anthropic.com.',
  },
  {
    kind: 'openai',
    label: 'OpenAI (GPT)',
    placeholder: 'sk-…',
    hint: 'Broadest model catalogue. Get a key at platform.openai.com.',
  },
  {
    kind: 'google',
    label: 'Google (Gemini)',
    placeholder: 'AI…',
    hint: 'Get a key at aistudio.google.com.',
  },
  {
    kind: 'ollama',
    label: 'Ollama (self-hosted)',
    placeholder: 'any value (local)',
    hint: 'No API key needed; set base URL to http://localhost:11434.',
  },
];

export function OnboardingRoute(): JSX.Element {
  const navigate = useNavigate();
  const organizationId = useAuthStore((s) => s.organizationId);
  const projects = useProjectStore((s) => s.projects);
  const setProjects = useProjectStore((s) => s.setProjects);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);

  const [step, setStep] = useState<Step>('welcome');

  // If the user already has BOTH a provider AND a project, they're past
  // onboarding — redirect out.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const providers = await shaktiApi.listProviders();
        if (!cancelled && providers.length > 0 && projects.length > 0) {
          navigate({ to: '/dashboard' });
        }
      } catch {
        // Ignore — the onboarding path is safe to run.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, projects.length]);

  const totalSteps = 4;
  const stepIndex =
    step === 'welcome'
      ? 1
      : step === 'connect_llm'
        ? 2
        : step === 'create_project'
          ? 3
          : 4;
  const [firstTaskProject, setFirstTaskProject] = useState<{ id: string; name: string } | null>(
    null,
  );

  return (
    <main
      className="grid min-h-screen place-items-center bg-bg-base text-fg-primary"
      aria-labelledby="onboarding-title"
    >
      <section className="shakti-card flex w-full max-w-2xl flex-col gap-6 p-8" data-testid="onboarding-card">
        <header className="flex flex-col gap-2">
          <span className="shakti-mono text-2xs uppercase tracking-wider text-fg-muted">
            Shakti setup — step {stepIndex} of {totalSteps}
          </span>
          <ProgressBar current={stepIndex} total={totalSteps} />
        </header>

        {step === 'welcome' ? (
          <WelcomeStep onNext={() => setStep('connect_llm')} />
        ) : step === 'connect_llm' ? (
          <ConnectLlmStep
            onNext={() => setStep('create_project')}
            onSkip={() => setStep('create_project')}
          />
        ) : step === 'create_project' ? (
          <CreateProjectStep
            organizationId={organizationId}
            onNext={(project) => {
              setProjects([
                ...projects,
                {
                  ...project,
                  organization_id: organizationId ?? '',
                },
              ]);
              setActiveProject(project.id);
              // #122 (round-6 close-out) — advance to the "first task"
              // step rather than bouncing straight to the dashboard.
              setFirstTaskProject({ id: project.id, name: project.name });
              setStep('first_task');
            }}
          />
        ) : step === 'first_task' && firstTaskProject ? (
          <FirstTaskStep
            project={firstTaskProject}
            onDone={() => {
              setStep('done');
              navigate({ to: '/dashboard' });
            }}
            onSkip={() => {
              setStep('done');
              navigate({ to: '/dashboard' });
            }}
          />
        ) : null}
      </section>
    </main>
  );
}

function ProgressBar({ current, total }: { current: number; total: number }): JSX.Element {
  return (
    <div className="flex gap-1" role="progressbar" aria-valuenow={current} aria-valuemin={1} aria-valuemax={total}>
      {Array.from({ length: total }).map((_, i) => (
        <div
          key={i}
          className={`h-1 flex-1 rounded-sm ${
            i < current ? 'bg-fg-primary' : 'bg-border-subtle'
          }`}
        />
      ))}
    </div>
  );
}

function WelcomeStep({ onNext }: { onNext: () => void }): JSX.Element {
  return (
    <>
      <h1 id="onboarding-title" className="text-2xl font-semibold">
        Let's get you shipping in 4 clicks.
      </h1>
      <p className="text-sm text-fg-secondary">
        Shakti turns a natural-language product intent into deployed code through a 12-phase
        governed SDLC. You'll own the code, the audit trail, and the LLM keys — Shakti is BYOK.
      </p>
      <p className="text-sm text-fg-secondary">
        Next you'll connect an LLM provider, then create your first project. Takes about two
        minutes.
      </p>
      <div className="flex justify-end">
        <button
          type="button"
          onClick={onNext}
          className="rounded-sm bg-fg-primary px-4 py-2 text-sm font-semibold text-bg-base"
          data-testid="onboarding-welcome-next"
        >
          Let's go →
        </button>
      </div>
    </>
  );
}

function ConnectLlmStep({
  onNext,
  onSkip,
}: {
  onNext: () => void;
  onSkip: () => void;
}): JSX.Element {
  const [kind, setKind] = useState<string>(PROVIDER_CHOICES[0]?.kind ?? 'anthropic');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choice = PROVIDER_CHOICES.find((p) => p.kind === kind);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await shaktiApi.registerProvider({
        provider: kind,
        api_key: apiKey.trim(),
        base_url: baseUrl.trim() || null,
      });
      onNext();
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className="text-xl font-semibold">Connect an LLM provider</h2>
      <p className="text-sm text-fg-secondary">
        Shakti never stores your key in plaintext — it's encrypted at rest with AES-256-GCM and
        scoped to your organisation. You can rotate or revoke it anytime from Settings.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-xs">
          Provider
          <select
            value={kind}
            onChange={(e) => {
              setKind(e.target.value);
              setBaseUrl('');
            }}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            disabled={busy}
            data-testid="onboarding-provider-kind"
          >
            {PROVIDER_CHOICES.map((p) => (
              <option key={p.kind} value={p.kind}>
                {p.label}
              </option>
            ))}
          </select>
          {choice ? <span className="text-2xs text-fg-muted">{choice.hint}</span> : null}
        </label>
        <label className="flex flex-col gap-1 text-xs">
          API key
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={choice?.placeholder ?? 'sk-…'}
            required
            disabled={busy}
            className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="onboarding-provider-apikey"
          />
        </label>
        {kind === 'ollama' ? (
          <label className="flex flex-col gap-1 text-xs">
            Base URL
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="http://localhost:11434"
              disabled={busy}
              className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            />
          </label>
        ) : null}
        {error ? (
          <div
            role="alert"
            className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
          >
            {error}
          </div>
        ) : null}
        <div className="mt-2 flex items-center justify-between">
          <button
            type="button"
            onClick={onSkip}
            className="text-xs text-fg-muted underline"
            disabled={busy}
            data-testid="onboarding-provider-skip"
          >
            Skip for now (you won't be able to start a build)
          </button>
          <button
            type="submit"
            disabled={busy || apiKey.trim().length < 8}
            className="rounded-sm bg-fg-primary px-4 py-2 text-sm font-semibold text-bg-base disabled:opacity-50"
            data-testid="onboarding-provider-submit"
          >
            {busy ? 'Connecting…' : 'Connect →'}
          </button>
        </div>
      </form>
    </>
  );
}

function CreateProjectStep({
  organizationId,
  onNext,
}: {
  organizationId: string | null;
  onNext: (p: { id: string; name: string; slug: string }) => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    if (!organizationId) {
      setError('Not signed in — cannot create a project.');
      return;
    }
    setBusy(true);
    try {
      const project = await shaktiApi.createProject({
        name: name.trim(),
        slug: slug.trim() || autoSlug(name),
      });
      onNext({ id: project.id, name: project.name, slug: project.slug });
    } catch (e) {
      setError(friendlyError(e));
      setBusy(false);
    }
  }

  return (
    <>
      <h2 className="text-xl font-semibold">Create your first project</h2>
      <p className="text-sm text-fg-secondary">
        A project holds a set of related builds. You can add more later from the nav sidebar.
      </p>
      <form onSubmit={submit} className="flex flex-col gap-3">
        <label className="flex flex-col gap-1 text-xs">
          Name
          <input
            type="text"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slug || slug === autoSlug(name)) setSlug(autoSlug(e.target.value));
            }}
            placeholder="Aurora SaaS"
            required
            disabled={busy}
            className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="onboarding-project-name"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs">
          Slug
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toLowerCase())}
            placeholder="aurora-saas"
            pattern="[a-z0-9][a-z0-9\-]*"
            title="Lowercase letters, digits, hyphens"
            disabled={busy}
            className="shakti-mono rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-sm"
            data-testid="onboarding-project-slug"
          />
        </label>
        {error ? (
          <div
            role="alert"
            className="rounded-sm border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-200"
          >
            {error}
          </div>
        ) : null}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={busy || name.trim().length < 2}
            className="rounded-sm bg-fg-primary px-4 py-2 text-sm font-semibold text-bg-base disabled:opacity-50"
            data-testid="onboarding-project-submit"
          >
            {busy ? 'Creating…' : 'Create project →'}
          </button>
        </div>
      </form>
    </>
  );
}

function autoSlug(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function friendlyError(e: unknown): string {
  if (e instanceof ApiError) {
    return `${e.body?.error ?? e.message} (${e.status})`;
  }
  if (e instanceof Error) return e.message;
  return 'Unexpected error';
}

// #122 (round-6 close-out) — fourth onboarding step lets the user
// seed their first intent into the newly-created project.
// On submit we:
//   1. POST /v1/packets                   → create the packet
//   2. POST /v1/packets/{id}/commit-memory → lock the canonical intent
//   3. POST /v1/packets/{id}/start-building → kick the pipeline
// Each step is best-effort: when the backend is running in a fresh
// demo environment without providers yet, the user gets a skippable
// fallback so they can return to this step from Command Center.
function FirstTaskStep({
  project,
  onDone,
  onSkip,
}: {
  project: { id: string; name: string };
  onDone: () => void;
  onSkip: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [intent, setIntent] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!title.trim() || intent.trim().length < 20) {
      setError('Give this task a title and at least a paragraph of intent.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const packet = await shaktiApi.createPacket({
        charter_ref: `project:${project.id}`,
        title: title.trim(),
        intent: intent.trim(),
      });
      // Kick off the full pipeline — the server commits to memory and
      // transitions the Start Building gate inside /run-full-pipeline.
      try {
        await shaktiApi.runFullPipeline(packet.id);
      } catch {
        /* best effort — user can start it manually from Command Center */
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to start first task');
      setBusy(false);
    }
  }

  return (
    <form className="flex flex-col gap-3" onSubmit={submit}>
      <h1 id="onboarding-title" className="text-2xl font-semibold">
        One last thing — describe your first task for {project.name}
      </h1>
      <p className="text-sm text-fg-secondary">
        Shakti's 12-phase SDLC turns this description into a governed delivery
        run. You can approve, pause, or cancel at any checkpoint.
      </p>
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-fg-muted">Task title</span>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Build a reimbursement webapp"
          className="rounded-sm border border-border-subtle bg-bg-surface p-2 text-sm"
        />
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="text-fg-muted">Intent (a paragraph is enough)</span>
        <textarea
          rows={6}
          value={intent}
          onChange={(e) => setIntent(e.target.value)}
          placeholder="A lightweight expense-claim webapp for a 30-person team. Node.js backend, React frontend, Postgres, email notifications on approvals."
          className="rounded-sm border border-border-subtle bg-bg-surface p-2 text-sm"
        />
      </label>
      {error && (
        <div role="alert" className="text-xs text-status-error">
          {error}
        </div>
      )}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={onSkip}
          className="rounded-sm border border-border-subtle px-3 py-1.5 text-xs text-fg-secondary"
        >
          Skip for now
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded-sm bg-fg-primary px-4 py-2 text-sm font-semibold text-bg-base disabled:opacity-50"
        >
          {busy ? 'Starting…' : 'Start building →'}
        </button>
      </div>
    </form>
  );
}
